import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement} [productCountCurrent] - The element displaying the current visible product count.
 * @property {HTMLElement} [productCountTotal] - The element displaying the total product count.
 * @property {HTMLElement} [productCountProgress] - The progress bar wrapper element.
 * @property {HTMLElement} [productCountProgressBar] - The progress bar fill element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {Set<number>} */
  #renderedPages = new Set();

  /** @type {boolean} */
  #isRenderingPrevious = false;

  /** @type {boolean} */
  #isRenderingNext = false;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {(event: Event) => void} */
  #handlePageShow = (event) => {
    // BFCache restore can re-trigger observers while DOM already contains loaded pages.
    // Rebuild rendered page state to avoid loading the same page twice.
    // @ts-ignore - persisted exists on pageshow events in browsers.
    if (event?.persisted) {
      this.#syncRenderedPagesFromDom();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.loadMoreButton = this.refs.loadMoreButton;
    this.#syncRenderedPagesFromDom();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#setupLoadMoreButton();

    window.addEventListener('pageshow', this.#handlePageShow);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('pageshow', this.#handlePageShow);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Sets up the load more button click handler
   */
  #setupLoadMoreButton() {

    if (!this.loadMoreButton) return;
    
    this.loadMoreButton.addEventListener('click', this.#handleLoadMore.bind(this));
  }

  /**
   * Handles the load more button click
   */
  async #handleLoadMore() {
    const { grid } = this.refs;

    if (!this.loadMoreButton || !grid) return;

    // Capture the button reference at click time so we always restore the
    // exact button the user clicked, even if `this.loadMoreButton` is
    // reassigned later in the success path.
    const button = this.loadMoreButton;

    button.classList.add('loading');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
    }

    /** Restore the original button if it's still in the DOM (e.g. on early return / no replacement). */
    const restoreButton = () => {
      if (!button.isConnected) return;
      button.classList.remove('loading');
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
      }
    };

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      // Append the new products in chunks to avoid blocking the main thread
      // when the page contains many product cards. Each chunk yields back to
      // the browser via rAF so layout/paint/connectedCallback work for the
      // previous batch can flush before the next batch is inserted.
      await this.#appendInChunks(grid, [...nextPageItemElements]);

      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();
      
      // Update URL
      history.pushState('', '', nextPage.url.toString());

      // Get the new load more button from the fetched page (if it exists)
      const pageHTML = this.pages.get(nextPage.page);
      if (pageHTML) {
        const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
        const newLoadMoreContainer = parsedPage.querySelector('.load-more-container');

        // Remove the old button container
        const oldContainer = button.closest('.load-more-container');
        if (oldContainer) {
          if (newLoadMoreContainer) {
            // Replace with new button
            oldContainer.replaceWith(newLoadMoreContainer);

            this.loadMoreButton = newLoadMoreContainer.querySelector('.load-more-button');
            // Re-setup the event listener on the new button
            this.#setupLoadMoreButton();
          } else {
            // No more pages, remove the container
            oldContainer.remove();
          }
        }
      }

      // Prefetch the next page
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      // If the success path replaced the button container, the captured
      // `button` is no longer connected and `restoreButton` is a no-op.
      // Otherwise we always reset the loading/disabled state so the UI never
      // appears frozen, even on early returns.
      restoreButton();
    }
  }

  /**
   * Append nodes to a parent in small chunks, yielding to the browser between
   * chunks so we don't block the main thread when many custom elements need
   * to upgrade at once (each product card spawns several nested components).
   *
   * @param {Element} parent
   * @param {Element[]} nodes
   * @param {number} [chunkSize]
   */
  async #appendInChunks(parent, nodes, chunkSize = 6) {
    if (nodes.length <= chunkSize) {
      parent.append(...nodes);
      return;
    }

    for (let i = 0; i < nodes.length; i += chunkSize) {
      parent.append(...nodes.slice(i, i + chunkSize));
      // Yield to the browser between chunks. rAF is enough to let layout +
      // custom element connectedCallbacks for the previous chunk flush.
      if (i + chunkSize < nodes.length) {
        await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
      }
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#isRenderingNext) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#renderedPages.has(nextPage.page)) return;

    this.#isRenderingNext = true;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);

      this.#renderedPages.add(nextPage.page);

      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      history.pushState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#isRenderingNext = false;
    }
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPrevious) return;

    const { grid, viewMorePrevious } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) {
      if (viewMorePrevious) viewMorePrevious.remove();
      return;
    }

    if (this.#renderedPages.has(previousPage.page)) return;

    this.#isRenderingPrevious = true;

    // Show loading state
    if (viewMorePrevious) viewMorePrevious.setAttribute('aria-busy', 'true');

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) {
          return;
        }
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#renderedPages.add(previousPage.page);

      this.#aspectRatioHelper?.processNewElements();
      this.#updateProductCount();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      if (viewMorePrevious) viewMorePrevious.removeAttribute('aria-busy');
      this.#isRenderingPrevious = false;
    }
  }

  /**
   * Updates the visible product count text and progress bar
   * (e.g. "You've viewed X of Y products").
   */
  #updateProductCount() {
    const { grid, productCountCurrent, productCountTotal, productCountProgress, productCountProgressBar } = this.refs;
    if (!grid) return;

    const visibleCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;

    if (productCountCurrent) {
      productCountCurrent.textContent = String(visibleCount);
    }

    const totalCount = Number(productCountTotal?.textContent) || visibleCount;
    const percent = totalCount > 0 ? Math.min(100, (visibleCount / totalCount) * 100) : 0;

    if (productCountProgressBar) {
      productCountProgressBar.style.width = `${percent}%`;
    }

    if (productCountProgress) {
      productCountProgress.setAttribute('aria-valuenow', String(visibleCount));
    }
  }

  #syncRenderedPagesFromDom() {
    this.#renderedPages.clear();

    const { cards } = this.refs;
    if (!Array.isArray(cards)) return;

    for (const card of cards) {
      const page = Number(card.dataset.page);
      if (!Number.isNaN(page)) {
        this.#renderedPages.add(page);
      }
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#renderedPages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#syncRenderedPagesFromDom();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
